import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Wallet, TrendingUp, Zap, Shield, RefreshCw, ExternalLink,
  CheckCircle2, AlertTriangle, ArrowUpRight, Copy, Activity,
  BarChart2, Target, Clock, ChevronDown, ChevronUp,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── helpers ────────────────────────────────────────────────────────────────
const fmt = (n, dec = 2) => (parseFloat(n) || 0).toFixed(dec);
const pnlClass = (n) => (parseFloat(n) > 0 ? 'text-green-400' : parseFloat(n) < 0 ? 'text-red-400' : 'text-slate-400');

function StatCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <Card className="bg-slate-800/60 border-slate-700">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-slate-400 mb-1">{label}</p>
            <p className={`text-xl font-bold font-mono truncate ${accent || 'text-white'}`}>{value}</p>
            {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
          </div>
          <div className="ml-3 p-2 rounded-lg bg-slate-700/50">
            <Icon className="w-4 h-4 text-slate-400" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PnlRow({ label, value, icon: Icon }) {
  const cls = pnlClass(value);
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-700/50 last:border-0">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5 text-slate-500" />}
        <span className="text-sm text-slate-300">{label}</span>
      </div>
      <span className={`text-sm font-mono font-semibold ${cls}`}>
        {parseFloat(value) >= 0 ? '+' : ''}${fmt(value)}
      </span>
    </div>
  );
}

// ── main component ─────────────────────────────────────────────────────────
export default function BotEarnings() {
  const { user, isAdmin, isSubUser } = useAuth();

  const [earnings, setEarnings]       = useState(null);
  const [loading, setLoading]         = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing]   = useState(false);

  // withdraw state
  const [withdrawAmt, setWithdrawAmt]   = useState('');
  const [withdrawAddr, setWithdrawAddr] = useState('');
  const [withdrawing, setWithdrawing]   = useState(false);
  const [lastTx, setLastTx]             = useState(null);

  // ZK vault (admin)
  const [zkAmount, setZkAmount]   = useState('');
  const [zkNote, setZkNote]       = useState(null);
  const [zkLoading, setZkLoading] = useState(false);

  // history expand
  const [historyExpanded, setHistoryExpanded] = useState(false);

  const intervalRef = useRef(null);

  // ── fetch earnings ─────────────────────────────────────────────────────
  const fetchEarnings = useCallback(async (silent = true) => {
    if (!silent) setRefreshing(true);
    try {
      const endpoint = isSubUser ? '/api/subuser/earnings' : '/api/bot/earnings';
      const { data } = await axios.get(`${API_URL}${endpoint}`, { withCredentials: true });
      setEarnings(data);
      setLastUpdated(new Date());
    } catch {
      if (!silent) toast.error('Failed to load earnings');
    } finally {
      setLoading(false);
      if (!silent) setRefreshing(false);
    }
  }, [isSubUser]);

  useEffect(() => {
    fetchEarnings(false);
    // Poll every 5 seconds for near-real-time feel
    intervalRef.current = setInterval(() => fetchEarnings(true), 5000);
    return () => clearInterval(intervalRef.current);
  }, [fetchEarnings]);

  // Auto-fill connected Solana wallet
  useEffect(() => {
    if (user?.wallets?.length > 0 && !withdrawAddr) {
      const sol = user.wallets.find(w => w.chain === 'solana');
      if (sol?.address) setWithdrawAddr(sol.address);
    }
  }, [user, withdrawAddr]);

  // ── withdraw ───────────────────────────────────────────────────────────
  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmt);
    if (!amount || amount < 0.5) { toast.error('Minimum $0.50'); return; }
    if (!withdrawAddr || withdrawAddr.length < 32) { toast.error('Enter valid Solana address'); return; }

    const available = parseFloat(earnings?.balance || 0);
    if (amount > available) { toast.error(`Only $${fmt(available)} available`); return; }

    setWithdrawing(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/bot/earnings/withdraw-direct`,
        { wallet_address: withdrawAddr, amount_usd: amount },
        { withCredentials: true }
      );
      setLastTx(data);
      setWithdrawAmt('');
      toast.success(`✅ $${fmt(amount)} sent on-chain! Verify on Solscan.`);
      fetchEarnings(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Withdrawal failed');
    } finally {
      setWithdrawing(false);
    }
  };

  // ── ZK vault transfer (admin only) ────────────────────────────────────
  const handleZkTransfer = async () => {
    const amount = parseFloat(zkAmount);
    if (!amount || amount < 0.5) { toast.error('Minimum $0.50'); return; }
    setZkLoading(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/bot/earnings/zk-transfer`,
        { amount_usd: amount },
        { withCredentials: true }
      );
      setZkNote(data.zk_note || data.note || null);
      setZkAmount('');
      toast.success('Earnings moved to ZK vault');
      fetchEarnings(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'ZK transfer failed');
    } finally {
      setZkLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success('Copied!')).catch(() => toast.error('Copy failed'));
  };

  // ── loading skeleton ───────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <div className="h-8 bg-slate-700 rounded animate-pulse w-48" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-24 bg-slate-700 rounded-lg animate-pulse" />
          ))}
        </div>
        <div className="h-48 bg-slate-700 rounded-lg animate-pulse" />
      </div>
    );
  }

  // ── derived values ─────────────────────────────────────────────────────
  const balance          = parseFloat(earnings?.balance          || 0);
  const totalEarned      = parseFloat(earnings?.total_earned     || 0);
  const totalWithdrawn   = parseFloat(earnings?.total_withdrawn  || 0);
  const totalPnl         = parseFloat(earnings?.total_pnl        || 0);
  const unrealizedPnl    = parseFloat(earnings?.unrealized_pnl   || 0);
  const volumeBotEarnings= parseFloat(earnings?.volume_bot_earnings || 0);
  const lureBotEarnings  = parseFloat(earnings?.lure_bot_earnings  || 0);
  const history          = earnings?.history || [];
  const visibleHistory   = historyExpanded ? history : history.slice(0, 5);

  const lastUpdatedStr = lastUpdated
    ? lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '--';

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-5xl mx-auto">

      {/* ── header ── */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-400" />
            Bot Earnings
          </h1>
          <p className="text-xs text-slate-400 mt-0.5">
            Auto-accumulating profits · Real on-chain withdrawals via Helius
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Updated {lastUpdatedStr}
          </span>
          <Button
            size="sm"
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-700 h-8 px-3"
            onClick={() => fetchEarnings(false)}
            disabled={refreshing}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* ── last tx confirmation ── */}
      {lastTx && (
        <Card className="border-green-600/40 bg-green-900/20">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-green-300">
                  {parseFloat(lastTx.sol_amount || 0).toFixed(6)} SOL (${fmt(lastTx.amount_usd)}) sent on-chain ✅
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-xs text-slate-400 font-mono truncate">{lastTx.tx_signature}</p>
                  <button onClick={() => copyToClipboard(lastTx.tx_signature)} className="shrink-0">
                    <Copy className="w-3 h-3 text-slate-500 hover:text-slate-300" />
                  </button>
                  {lastTx.solscan_url && (
                    <a href={lastTx.solscan_url} target="_blank" rel="noopener noreferrer" className="shrink-0">
                      <ExternalLink className="w-3 h-3 text-blue-400 hover:text-blue-300" />
                    </a>
                  )}
                </div>
              </div>
              <button onClick={() => setLastTx(null)} className="text-slate-500 hover:text-slate-300 text-xs">✕</button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── stat cards ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          icon={Wallet}
          label="Available Balance"
          value={`$${fmt(balance)}`}
          sub="Withdrawable profits"
          accent={balance > 0 ? 'text-green-400' : 'text-white'}
        />
        <StatCard
          icon={TrendingUp}
          label="Total Earned"
          value={`$${fmt(totalEarned)}`}
          sub="All-time realized"
        />
        <StatCard
          icon={ArrowUpRight}
          label="Total Withdrawn"
          value={`$${fmt(totalWithdrawn)}`}
          sub="Sent on-chain"
        />
        <StatCard
          icon={Activity}
          label="Unrealized PnL"
          value={`${unrealizedPnl >= 0 ? '+' : ''}$${fmt(unrealizedPnl)}`}
          sub="Open positions"
          accent={pnlClass(unrealizedPnl)}
        />
      </div>

      {/* ── earnings breakdown ── */}
      <Card className="bg-slate-800/60 border-slate-700">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-sm text-slate-300 flex items-center gap-2">
            <BarChart2 className="w-4 h-4" /> Earnings Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <PnlRow label="VolumeBot Realized PnL"  value={volumeBotEarnings} icon={Zap} />
          <PnlRow label="LureBot Realized PnL"    value={lureBotEarnings}   icon={Target} />
          <PnlRow label="Total Realized PnL"      value={totalPnl}          icon={TrendingUp} />
          <PnlRow label="Unrealized PnL (open)"   value={unrealizedPnl}     icon={Activity} />
          <div className="mt-3 pt-3 border-t border-slate-600 flex items-center justify-between">
            <span className="text-xs text-slate-500">
              Paper PnL is tracked separately and is <strong className="text-slate-400">not withdrawable</strong>
            </span>
            <span className="text-xs px-2 py-0.5 rounded bg-slate-700 text-slate-400">Live mode only</span>
          </div>
        </CardContent>
      </Card>

      {/* ── withdraw + ZK side by side ── */}
      <div className={`grid gap-4 ${isAdmin ? 'md:grid-cols-2' : 'md:grid-cols-1 max-w-xl'}`}>

        {/* withdraw card */}
        <Card className="bg-slate-800/60 border-slate-700">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm text-slate-300 flex items-center gap-2">
              <Wallet className="w-4 h-4" /> Withdraw to Solana Wallet
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <div className="text-xs text-slate-400 space-y-0.5">
              <p>• Real SOL transferred on-chain via Helius mainnet</p>
              <p>• Verifiable on Solscan within seconds</p>
              <p className="text-yellow-400/80">
                • {isAdmin ? '✅ Admin — no credit fee' : '5 credits deducted per withdrawal'}
              </p>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-slate-400">Amount (USD)</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  placeholder="0.00"
                  value={withdrawAmt}
                  onChange={e => setWithdrawAmt(e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white h-9 text-sm"
                  min="0.5"
                  step="0.01"
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="border-slate-600 text-slate-300 hover:bg-slate-700 h-9 px-2 text-xs whitespace-nowrap"
                  onClick={() => setWithdrawAmt(fmt(balance))}
                >
                  Max
                </Button>
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-slate-400">Solana Wallet Address</Label>
              <Input
                placeholder="Solana address..."
                value={withdrawAddr}
                onChange={e => setWithdrawAddr(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white h-9 text-sm font-mono"
              />
              {withdrawAddr.length > 8 && (
                <p className="text-xs text-slate-500">
                  → {withdrawAddr.slice(0, 8)}...{withdrawAddr.slice(-6)}
                </p>
              )}
            </div>

            <Button
              className="w-full bg-green-600 hover:bg-green-500 text-white h-9 text-sm font-semibold"
              onClick={handleWithdraw}
              disabled={withdrawing || !withdrawAmt || balance <= 0}
            >
              {withdrawing ? (
                <><RefreshCw className="w-3.5 h-3.5 mr-2 animate-spin" /> Sending...</>
              ) : (
                <><ArrowUpRight className="w-3.5 h-3.5 mr-2" /> Withdraw ${withdrawAmt || '0.00'}</>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* ZK vault (admin only) */}
        {isAdmin && (
          <Card className="bg-slate-800/60 border-slate-700">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-sm text-slate-300 flex items-center gap-2">
                <Shield className="w-4 h-4" /> ZK Vault Transfer
                <span className="ml-auto text-xs px-1.5 py-0.5 rounded bg-purple-800/50 text-purple-300">Admin</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-3">
              <div className="text-xs text-slate-400 space-y-0.5">
                <p>• Move earnings for anonymous withdrawal</p>
                <p>• Get a ZK-Note to withdraw to any wallet</p>
                <p>• 0.5% protocol fee on withdrawal</p>
              </div>

              {zkNote && (
                <div className="p-2 rounded bg-purple-900/30 border border-purple-700/40">
                  <p className="text-xs text-purple-300 font-semibold mb-1">ZK-Note (save this!):</p>
                  <div className="flex items-center gap-2">
                    <p className="text-xs font-mono text-slate-300 truncate">{zkNote}</p>
                    <button onClick={() => copyToClipboard(zkNote)}>
                      <Copy className="w-3 h-3 text-slate-400 hover:text-slate-200 shrink-0" />
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-1">
                <Label className="text-xs text-slate-400">Amount (USD)</Label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={zkAmount}
                  onChange={e => setZkAmount(e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white h-9 text-sm"
                  min="0.5"
                />
              </div>

              <Button
                className="w-full bg-purple-700 hover:bg-purple-600 text-white h-9 text-sm font-semibold"
                onClick={handleZkTransfer}
                disabled={zkLoading || !zkAmount || balance <= 0}
              >
                {zkLoading ? (
                  <><RefreshCw className="w-3.5 h-3.5 mr-2 animate-spin" /> Processing...</>
                ) : (
                  <><Shield className="w-3.5 h-3.5 mr-2" /> Move to ZK Vault</>
                )}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* ── earnings history ── */}
      {history.length > 0 && (
        <Card className="bg-slate-800/60 border-slate-700">
          <CardHeader className="pb-2 pt-4 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm text-slate-300 flex items-center gap-2">
                <Clock className="w-4 h-4" /> Earnings History
                <span className="text-xs text-slate-500">({history.length})</span>
              </CardTitle>
              <button
                onClick={() => setHistoryExpanded(p => !p)}
                className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1"
              >
                {historyExpanded ? <><ChevronUp className="w-3 h-3" /> Less</> : <><ChevronDown className="w-3 h-3" /> More</>}
              </button>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="space-y-1">
              {visibleHistory.map((item, idx) => {
                const amt = parseFloat(item.amount || item.pnl_usd || 0);
                const isWithdraw = (item.type || '').includes('withdraw');
                return (
                  <div
                    key={idx}
                    className="flex items-center justify-between py-2 border-b border-slate-700/40 last:border-0 text-sm"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {isWithdraw
                        ? <ArrowUpRight className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                        : <TrendingUp className="w-3.5 h-3.5 text-green-400 shrink-0" />
                      }
                      <div className="min-w-0">
                        <p className="text-slate-300 capitalize truncate">
                          {(item.type || 'trade').replace(/_/g, ' ')}
                          {item.token && <span className="text-slate-500 ml-1">· {item.token}</span>}
                        </p>
                        <p className="text-xs text-slate-500">
                          {item.timestamp ? new Date(item.timestamp).toLocaleString() : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-2">
                      <span className={`font-mono font-semibold ${isWithdraw ? 'text-blue-400' : pnlClass(amt)}`}>
                        {isWithdraw ? '-' : amt >= 0 ? '+' : ''}${Math.abs(amt).toFixed(4)}
                      </span>
                      {item.tx_signature && (
                        <a
                          href={`https://solscan.io/tx/${item.tx_signature}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <ExternalLink className="w-3 h-3 text-slate-500 hover:text-blue-400" />
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── empty state ── */}
      {!loading && history.length === 0 && balance === 0 && (
        <Card className="bg-slate-800/60 border-slate-700">
          <CardContent className="py-12 flex flex-col items-center text-center gap-3">
            <AlertTriangle className="w-10 h-10 text-slate-600" />
            <p className="text-slate-400 font-medium">No earnings yet</p>
            <p className="text-sm text-slate-500 max-w-xs">
              Start a VolumeBot or LureBot in <strong>Live mode</strong> to accumulate real profits here.
              Paper-mode profits are tracked but not withdrawable.
            </p>
          </CardContent>
        </Card>
      )}

    </div>
  );
}
